import { BaseColorProperties } from '../../lib/liveformseditor/common-items/BaseItem/base-item-properties';
import {
  InteractiveColorProperties
} from '../../lib/liveformseditor/common-items/InteractiveItem/interactive-item-properties';

export const extractColorProperties = (
  properties: Record<string, any>
): BaseColorProperties & InteractiveColorProperties => {
  return {
    inputColor: properties['InputColor'] || '',
    textBgColor: properties['TextBgColor'] || ''
  };
};

export const BASE_TEXT_COLOR_VARIANTS = {
  white: `#FFFFFFFF`,
  black: `#000000FF`
} as const;

export type TextColorVariant = (typeof BASE_TEXT_COLOR_VARIANTS)[keyof typeof BASE_TEXT_COLOR_VARIANTS];

export const getColors = (properties: BaseColorProperties | InteractiveColorProperties) => {
  const { white } = BASE_TEXT_COLOR_VARIANTS;
  const textBgColor =
    'textBgColor' in properties && properties.textBgColor ? getArgbToRgba(properties.textBgColor) : white;
  const textColor = getTextColor(textBgColor);

  const inputBgColor =
    'inputColor' in properties && properties.inputColor ? getArgbToRgba(properties.inputColor) : white;
  const inputTextColor = getTextColor(inputBgColor);

  return {
    textBgColor,
    textColor,
    inputBgColor,
    inputTextColor
  };
};

export function getTextColor(bgColor: string): TextColorVariant {
  const { white, black } = BASE_TEXT_COLOR_VARIANTS;
  if (!bgColor) {
    return black;
  }

  const alpha = parseInt(bgColor.substring(7, 9), 16);

  if (alpha === 0) {
    return black;
  }

  const r = parseInt(bgColor.substring(1, 3), 16);
  const g = parseInt(bgColor.substring(3, 5), 16);
  const b = parseInt(bgColor.substring(5, 7), 16);

  return isDarkColor(r, g, b) ? white : black;
}

function isDarkColor(r: number, g: number, b: number): boolean {
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

export function getArgbToRgba(color: string) {
  if (!color || !color.startsWith('#')) {
    if (color) {
      console.warn('color format not supported', color);
    }

    return `#FF000000`;
  }

  const A = color.substring(1, 3);
  const R = color.substring(3, 5);
  const G = color.substring(5, 7);
  const B = color.substring(7, 9);

  return `#${R}${G}${B}${A}`.toUpperCase();
}

export function getHexOrRgbaToArgb(color: string): string {
  if (!color) {
    return '';
  }

  if (!color.startsWith('#')) {
    console.warn('color format not supported', color);
    return '';
  }

  const R = color.substring(1, 3);
  const G = color.substring(3, 5);
  const B = color.substring(5, 7);
  const A = color.substring(7, 9) || 'FF';

  return `#${A}${R}${G}${B}`.toUpperCase();
}

export const getTextColorsPropertiesStyle = (textBgColor: string, textColor?: string) => {
  const textStyle: { [key: string]: string } = {};

  if (textBgColor) {
    textStyle['backgroundColor'] = textBgColor;
  }

  if (textColor) {
    textStyle['color'] = textColor;
  }

  return textStyle;
};

export const getInputStyle = (inputColor: string | null, inputTextColor?: string) => {
  const inputStyle: { [key: string]: string } = {};

  if (inputColor) {
    inputStyle['backgroundColor'] = inputColor;
  }

  if (inputTextColor) {
    inputStyle['color'] = inputTextColor;
  }

  return inputStyle;
};
